import {
  Bar,
  BarChart,
  CartesianGrid,
  Rectangle,
  RectangleProps,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from "recharts";
import { SALES_DEFINITIONS } from "./salesDefinitions.ts";
import { ChartLegendItem } from "../../UI/Chart/ChartLegendItem.tsx";
import {
  AXIS_COLOR,
  AXIS_TICK,
  GRID_COLOR,
  SURFACE,
} from "../../UI/Chart/chartStyle.ts";
import { SalesGateway, sumOf } from "./SaleRecord.ts";
import {
  EXPECTED_COLLECTION_MONTHS,
  ExpectedCollectionsOfMonth,
} from "./salesBreakdowns.ts";
import { SALES_GATEWAY_COLORS } from "./salesLabels.ts";
import {
  formatBucketTick,
  formatCompactEuros,
  formatEuros,
  formatMonth,
} from "./salesFormat.ts";
import { SalesGranularity, SalesPeriod } from "./salesPeriod.ts";
import {
  EmptyStatistics,
  LoadingBlock,
  Stat,
  StatisticsCard,
} from "./StatisticsCard.tsx";
import { CsvButton } from "./CsvButton.tsx";
import { centsForCsv, downloadSalesCsv, toCsv } from "./salesCsv.ts";

const HOVERED_MONTH_WASH = "#f0efec";
const STRIPE_INSTALLMENTS_COLOR = SALES_GATEWAY_COLORS[SalesGateway.Stripe];
const AWAITING_TRANSFERS_COLOR = SALES_GATEWAY_COLORS[SalesGateway.Bonifico];
const TOP_CORNERS_RADIUS: [number, number, number, number] = [4, 4, 0, 0];
const NO_RADIUS: [number, number, number, number] = [0, 0, 0, 0];

type ChartMonth = ExpectedCollectionsOfMonth & { tick: string };

function totalOf(month: ExpectedCollectionsOfMonth): number {
  return month.stripeInstallmentsInCents + month.awaitingTransfersInCents;
}

function ExpectedTooltip({ active, payload }: TooltipProps<number, string>) {
  const month = payload?.[0]?.payload as ChartMonth | undefined;
  if (!active || !month) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm px-3 py-2 text-xs flex flex-col gap-1 min-w-48">
      <span className="font-semibold text-gray-700">
        {formatMonth(month.month)}
      </span>
      {month.awaitingTransfersInCents > 0 && (
        <span className="flex justify-between gap-4 text-gray-500">
          Bonifici in attesa
          <span className="font-medium text-gray-800">
            {formatEuros(month.awaitingTransfersInCents)}
          </span>
        </span>
      )}
      <span className="flex justify-between gap-4 text-gray-500">
        Rate Stripe
        <span className="font-medium text-gray-800">
          {formatEuros(month.stripeInstallmentsInCents)}
        </span>
      </span>
    </div>
  );
}

function ExpectedSegment({
  isTop,
  ...segment
}: RectangleProps & { isTop: boolean }) {
  return (
    <Rectangle {...segment} radius={isTop ? TOP_CORNERS_RADIUS : NO_RADIUS} />
  );
}

export function ExpectedCollectionsCard({
  months,
  period,
  isLoading,
}: {
  months: ExpectedCollectionsOfMonth[];
  period: SalesPeriod;
  isLoading: boolean;
}) {
  const chartMonths: ChartMonth[] = months.map((month) => ({
    ...month,
    tick: formatBucketTick(month.month, SalesGranularity.Month),
  }));
  const totalInCents = sumOf(months, totalOf);
  const awaitingTransfersInCents = sumOf(
    months,
    (month) => month.awaitingTransfersInCents,
  );

  const exportCsv = () =>
    downloadSalesCsv({
      section: "incasso-previsto",
      period,
      csv: toCsv(months, [
        { header: "Mese", valueOf: (month) => formatMonth(month.month) },
        {
          header: "Rate Stripe (€)",
          valueOf: (month) => centsForCsv(month.stripeInstallmentsInCents),
        },
        {
          header: "Bonifici in attesa (€)",
          valueOf: (month) => centsForCsv(month.awaitingTransfersInCents),
        },
        {
          header: "Totale (€)",
          valueOf: (month) => centsForCsv(totalOf(month)),
        },
      ]),
    });

  return (
    <StatisticsCard
      title="Incasso previsto"
      info={SALES_DEFINITIONS.expectedCollections}
      description={`Rate Stripe attese e bonifici ancora da ricevere, nei prossimi ${EXPECTED_COLLECTION_MONTHS} mesi`}
      actions={totalInCents > 0 ? <CsvButton onClick={exportCsv} /> : undefined}
    >
      {isLoading ? (
        <LoadingBlock className="h-56 w-full" />
      ) : totalInCents === 0 ? (
        <EmptyStatistics>
          Nessun incasso previsto con i filtri attivi
        </EmptyStatistics>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-4">
            <Stat label="Totale previsto" value={formatEuros(totalInCents)} />
            <Stat
              label="Di cui bonifici in attesa"
              value={formatEuros(awaitingTransfersInCents)}
            >
              <span className="text-xs text-gray-500">
                Senza una data prevista: li mostro nel mese in corso
              </span>
            </Stat>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart
              data={chartMonths}
              margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
            >
              <CartesianGrid vertical={false} stroke={GRID_COLOR} />
              <XAxis
                dataKey="tick"
                tick={AXIS_TICK}
                tickLine={false}
                axisLine={{ stroke: AXIS_COLOR }}
                interval="preserveStartEnd"
                minTickGap={12}
              />
              <YAxis
                width={64}
                tick={AXIS_TICK}
                axisLine={false}
                tickLine={false}
                tickFormatter={(value: number) => formatCompactEuros(value)}
              />
              <Tooltip
                content={<ExpectedTooltip />}
                cursor={{ fill: HOVERED_MONTH_WASH }}
              />
              <Bar
                dataKey="stripeInstallmentsInCents"
                name="Rate Stripe"
                stackId="expected"
                fill={STRIPE_INSTALLMENTS_COLOR}
                stroke={SURFACE}
                strokeWidth={2}
                maxBarSize={32}
                isAnimationActive={false}
                shape={(shapeProps: unknown) => {
                  const segment = shapeProps as RectangleProps & {
                    payload: ChartMonth;
                  };
                  return (
                    <ExpectedSegment
                      {...segment}
                      isTop={segment.payload.awaitingTransfersInCents === 0}
                    />
                  );
                }}
              />
              <Bar
                dataKey="awaitingTransfersInCents"
                name="Bonifici in attesa"
                stackId="expected"
                fill={AWAITING_TRANSFERS_COLOR}
                stroke={SURFACE}
                strokeWidth={2}
                maxBarSize={32}
                radius={TOP_CORNERS_RADIUS}
                isAnimationActive={false}
              />
            </BarChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            <ChartLegendItem
              color={STRIPE_INSTALLMENTS_COLOR}
              label="Rate Stripe"
              shape="block"
            />
            <ChartLegendItem
              color={AWAITING_TRANSFERS_COLOR}
              label="Bonifici in attesa"
              shape="block"
            />
          </div>
        </div>
      )}
    </StatisticsCard>
  );
}
